import { observer } from 'mobx-react-lite';
import React, { useEffect } from 'react';
import { useParams } from 'react-router';

import GoBack from 'common/GoBack';
import PageSizer from 'common/PageSizer';
import TimezoneSelector from 'common/TimezoneSelector';
import { useActiveElements } from 'global/ActiveElements/useActiveElements';

import ActionLog from './components/ActionLog';
import AutobatchNotificationPopup from './components/AutobacthNotificationPopup';
import DataGrid from './components/DataGrid';
import DetailsHeader from './components/DetailsHeader';
import DriversWidget from './components/DriversWidget';
import MockDataToggle from './components/MockDataToggle';
import { rideDetailsStore } from './RideDetailsStore';
import { RIDE_DETAILS_TEXT } from './text';
import { getRideDetailsActiveElementConfig } from './utils';

import styles from './styles.module.css';

const PAGE_KEY = 'RIDE_DETAILS';

const RideDetails = observer(() => {
	const { rideId } = useParams();
	const active = useActiveElements();
	const {
		loading,
		error,
		details,
		stations,
		vehicleAssignments,
		workAssignmentSequences,
		setSelectedTimezone,
		selectedTimezone,
		stationTimezone,
	} = rideDetailsStore;

	useEffect(() => {
		rideDetailsStore.fetchInitialData(rideId);
	}, [rideId]);

	useEffect(() => {
		active.mountForPage(PAGE_KEY, getRideDetailsActiveElementConfig(rideDetailsStore));
		return () => active.cleanupPage(PAGE_KEY);
	}, [active]);

	// TODO: MockDataToggle is a dev-only escape hatch. It's mounted in all
	// three branches (error, loading, success) so the toggle is reachable
	// even when the real ride does not load. Consider lifting it into a
	// shared dev-only slot (root layout / dev overlay) once we have more
	// than one page that needs response-swapping in local dev.
	if (error)
		return (
			<>
				<div>{RIDE_DETAILS_TEXT.error}</div>
				<MockDataToggle />
			</>
		);
	if (loading)
		return (
			<>
				<div>{RIDE_DETAILS_TEXT.loading}</div>
				<MockDataToggle />
			</>
		);

	return (
		<>
			<PageSizer fullPage>
				<div className={styles.page}>
					<GoBack label={RIDE_DETAILS_TEXT.workAssignmentsList} />
					<DetailsHeader
						details={details}
						vehicleAssignments={vehicleAssignments}
						stations={stations}
						breakdown={details.breakdown}
						workAssignmentSequences={workAssignmentSequences}
					/>
				</div>
				<div className={styles.workAssignments}>
					<div className={styles.selector}>
						<TimezoneSelector
							onSelection={setSelectedTimezone}
							selected={selectedTimezone}
							additionalOptions={[stationTimezone]}
						/>
					</div>
					<DataGrid />
				</div>
				<div className={styles.page}>
					<ActionLog />
				</div>
				<AutobatchNotificationPopup />
			</PageSizer>
			<DriversWidget isAssignAllMode={false} onDriverSelectedAll={() => {}} />
			<MockDataToggle />
		</>
	);
});

export default RideDetails;
